import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { CreateTeamInput, DataEnvelope, ListEnvelope, Team, UpdateTeamInput, User } from './types';

export const teamsKey = ['teams'] as const;

export function useTeams() {
  return useQuery({
    queryKey: teamsKey,
    queryFn: ({ signal }) => api<DataEnvelope<Team[]>>('/teams', { signal }).then((r) => r.data),
  });
}

export function useTeamMembers(teamId: string | null) {
  return useQuery({
    queryKey: [...teamsKey, teamId, 'members'],
    enabled: !!teamId,
    queryFn: ({ signal }) =>
      api<ListEnvelope<User>>(`/teams/${teamId}/members`, { query: { limit: 100 }, signal }).then(
        (r) => r.data,
      ),
  });
}

function useInvalidateTeams() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: teamsKey });
    void qc.invalidateQueries({ queryKey: ['users'] });
  };
}

export function useCreateTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: (input: CreateTeamInput) =>
      api<DataEnvelope<Team>>('/teams', { method: 'POST', body: input }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useUpdateTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTeamInput }) =>
      api<DataEnvelope<Team>>(`/teams/${id}`, { method: 'PATCH', body: input }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useDeleteTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/teams/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useTeamMembership() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ teamId, userId, action }: { teamId: string; userId: string; action: 'add' | 'remove' }) =>
      action === 'add'
        ? api<void>(`/teams/${teamId}/members`, { method: 'POST', body: { userId } })
        : api<void>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
